import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff, Lock, Login } from "@mui/icons-material";
import { signIn, confirmSignIn } from "aws-amplify/auth";
import { AtomicLogo } from "../../../assets/AtomicLogo";
import {
  nextViewForStep,
  mfaErrorMessage,
  type SignInView,
  type TotpSetupDetails,
} from "../mfa_steps";

interface PasswordEntryProps {
  email: string;
  onSwitchToForgotPassword: () => void;
  onSwitchToLanding: () => void;
  onSignInSuccess: () => void;
}

export const PasswordEntryComponent = ({
  email,
  onSwitchToForgotPassword,
  onSwitchToLanding,
  onSignInSuccess,
}: PasswordEntryProps) => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [view, setView] = useState<SignInView["kind"]>("password");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [totpSetupDetails, setTotpSetupDetails] =
    useState<TotpSetupDetails | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [secretCopied, setSecretCopied] = useState(false);

  // Walks nextStep results (from signIn or confirmSignIn) to the view that
  // should render next, auto-resolving steps that need no user input of
  // their own (MFA selection, when TOTP is the only allowed method).
  const advance = async (result: {
    isSignedIn?: boolean;
    nextStep?: any;
  }): Promise<void> => {
    const signInView = nextViewForStep(result?.nextStep, result?.isSignedIn);

    switch (signInView.kind) {
      case "signed_in":
        onSignInSuccess();
        return;
      case "new_password":
        setView("new_password");
        return;
      case "totp_setup":
        setTotpSetupDetails(signInView.totpSetupDetails);
        setView("totp_setup");
        return;
      case "totp_code":
        setView("totp_code");
        return;
      case "mfa_selection":
        if (!signInView.allowedMFATypes.includes("TOTP")) {
          setError(
            "This account requires a multi-factor method that isn't supported here yet."
          );
          return;
        }
        await advance(await confirmSignIn({ challengeResponse: "TOTP" }));
        return;
      case "unsupported_step":
        setError("Additional authentication step is required. Please try again.");
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const result = await signIn({
        username: email,
        password: password,
      });
      await advance(result);
    } catch (err: any) {
      console.error("Sign in error:", err);

      // Handle specific error cases
      if (err.name === "NotAuthorizedException") {
        setError("Email or password wrong.");
      } else if (err.name === "UserNotConfirmedException") {
        setError("Please verify your email address before signing in.");
      } else if (err.name === "TooManyRequestsException") {
        setError("Too many failed attempts. Please try again later.");
      } else if (err.name === "InvalidPasswordException") {
        // This can happen when user has force change password status
        // and the temporary password is being validated
        const result = err as any;
        if (result?.nextStep) {
          await advance(result);
          return;
        }
        setError("Email or password wrong.");
      } else {
        // Check if this is a challenge response carried on the error
        const result = err as any;
        if (result?.nextStep) {
          await advance(result);
          return;
        }
        setError(
          err.message || "An error occurred during sign in. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmitNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    if (!newPassword || newPassword !== confirmNewPassword) {
      setError("Passwords do not match.");
      setLoading(false);
      return;
    }
    try {
      const result = await confirmSignIn({
        challengeResponse: newPassword,
      });
      await advance(result);
    } catch (err: any) {
      console.error("Confirm new password error:", err);
      setError(err?.message || "Failed to set new password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitMfaCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const result = await confirmSignIn({ challengeResponse: mfaCode });
      await advance(result);
    } catch (err: any) {
      console.error("Confirm MFA code error:", err);
      setError(
        mfaErrorMessage(err?.name) ||
          err?.message ||
          "Failed to verify code. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCopySecret = async () => {
    if (!totpSetupDetails) return;
    try {
      await navigator.clipboard.writeText(totpSetupDetails.sharedSecret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    } catch (err) {
      console.error("Copy secret error:", err);
    }
  };

  if (view === "totp_setup" && totpSetupDetails) {
    const setupUri = totpSetupDetails.getSetupUri("Atomic", email).toString();
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: "#fafafa",
          padding: 2,
        }}
      >
        <Card
          sx={{
            maxWidth: 400,
            width: "100%",
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
          }}
        >
          <CardContent sx={{ padding: 4 }}>
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Box sx={{ mb: 2 }}>
                <AtomicLogo width={64} height={64} />
              </Box>
              <Typography variant="h4" component="h1" gutterBottom>
                Set up an authenticator app
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Setup key"
              value={totpSetupDetails.sharedSecret}
              InputProps={{ readOnly: true }}
              margin="normal"
              sx={{ mb: 1 }}
            />
            <TextField
              fullWidth
              label="Setup link"
              value={setupUri}
              InputProps={{ readOnly: true }}
              margin="normal"
              sx={{ mb: 1 }}
            />
            <Button
              fullWidth
              variant="outlined"
              onClick={handleCopySecret}
              sx={{ mb: 2 }}
            >
              {secretCopied ? "Copied" : "Copy setup key"}
            </Button>

            <Box component="form" onSubmit={handleSubmitMfaCode}>
              <TextField
                fullWidth
                label="6-digit code"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                required
                margin="normal"
                inputMode="numeric"
                autoComplete="one-time-code"
                sx={{ mb: 2 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !mfaCode}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <Login />
                  )
                }
                sx={{
                  mb: 2,
                  py: 1.5,
                  backgroundColor: "#3877F7",
                  "&:hover": {
                    backgroundColor: "#2d5fd6",
                  },
                }}
              >
                {loading ? "Verifying..." : "Verify and Sign In"}
              </Button>

              <Box sx={{ textAlign: "center" }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={onSwitchToLanding}
                  sx={{ textDecoration: "none" }}
                >
                  ← Use a different email
                </Link>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (view === "totp_code") {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: "#fafafa",
          padding: 2,
        }}
      >
        <Card
          sx={{
            maxWidth: 400,
            width: "100%",
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
          }}
        >
          <CardContent sx={{ padding: 4 }}>
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Box sx={{ mb: 2 }}>
                <AtomicLogo width={64} height={64} />
              </Box>
              <Typography variant="h4" component="h1" gutterBottom>
                Enter your authenticator code
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmitMfaCode}>
              <TextField
                fullWidth
                label="6-digit code"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                required
                margin="normal"
                inputMode="numeric"
                autoComplete="one-time-code"
                sx={{ mb: 2 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !mfaCode}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <Login />
                  )
                }
                sx={{
                  mb: 2,
                  py: 1.5,
                  backgroundColor: "#3877F7",
                  "&:hover": {
                    backgroundColor: "#2d5fd6",
                  },
                }}
              >
                {loading ? "Verifying..." : "Verify and Sign In"}
              </Button>

              <Box sx={{ textAlign: "center" }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={onSwitchToLanding}
                  sx={{ textDecoration: "none" }}
                >
                  ← Use a different email
                </Link>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (view === "new_password") {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: "#fafafa",
          padding: 2,
        }}
      >
        <Card
          sx={{
            maxWidth: 400,
            width: "100%",
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
          }}
        >
          <CardContent sx={{ padding: 4 }}>
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Box sx={{ mb: 2 }}>
                <AtomicLogo width={64} height={64} />
              </Box>
              <Typography variant="h4" component="h1" gutterBottom>
                Set a new password
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your account requires a new password. Please create a secure
                password for {email}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmitNewPassword}>
              <TextField
                fullWidth
                label="New Password"
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1 }}
              />

              <TextField
                fullWidth
                label="Confirm New Password"
                type={showConfirmNewPassword ? "text" : "password"}
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={() =>
                          setShowConfirmNewPassword(!showConfirmNewPassword)
                        }
                        edge="end"
                      >
                        {showConfirmNewPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={
                  loading || !newPassword || newPassword !== confirmNewPassword
                }
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <Login />
                  )
                }
                sx={{
                  mb: 2,
                  py: 1.5,
                  backgroundColor: "#3877F7",
                  "&:hover": {
                    backgroundColor: "#2d5fd6",
                  },
                }}
              >
                {loading ? "Submitting..." : "Set Password and Sign In"}
              </Button>

              <Box sx={{ textAlign: "center" }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={onSwitchToLanding}
                  sx={{ textDecoration: "none" }}
                >
                  ← Use a different email
                </Link>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        background: "#fafafa",
        padding: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 400,
          width: "100%",
          borderRadius: 2,
          border: "1px solid #e0e0e0",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
        }}
      >
        <CardContent sx={{ padding: 4 }}>
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Box sx={{ mb: 2 }}>
              <AtomicLogo width={64} height={64} />
            </Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Welcome back
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enter your password for {email}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1 }}
            >
              If you were invited, use the temporary password provided to you
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSignIn}>
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={togglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            <Box sx={{ textAlign: "right", mb: 2 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={onSwitchToForgotPassword}
                sx={{ textDecoration: "none" }}
              >
                Forgot password?
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !password}
              startIcon={
                loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <Login />
                )
              }
              sx={{
                mb: 2,
                py: 1.5,
                backgroundColor: "#3877F7",
                "&:hover": {
                  backgroundColor: "#2d5fd6",
                },
              }}
            >
              {loading ? "Signing In..." : "Sign In"}
            </Button>

            <Box sx={{ textAlign: "center" }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={onSwitchToLanding}
                sx={{ textDecoration: "none" }}
              >
                ← Use a different email
              </Link>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
